import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as localizedFormat from 'dayjs/plugin/localizedFormat';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import * as isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as duration from 'dayjs/plugin/duration';
import * as isToday from 'dayjs/plugin/isToday';
import * as isBetween from 'dayjs/plugin/isBetween';
import { Injectable } from '@angular/core';

import 'dayjs/locale/en-gb.js';
import 'dayjs/locale/nb.js';
import { Dayjs } from 'dayjs';
import { isNullOrUndefined } from '../../shared/utils/object.utils';
import { getDefaultTimezone } from '../../shared/utils/timezone.utils';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(duration);
dayjs.extend(isToday);
dayjs.extend(isBetween);

export const LOCALIZED_FORMAT = 'L HH:mm';
export const LOCALIZED_FORMAT_WITHOUT_TIME = 'L';
export const ISO_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm';
export const FORMAT_MMM_YYYY = 'MMM YYYY';
export const FORMAT_EEE_MMMM_D_Y = 'EEE, MMMM d, y';
export const FORMAT_EEEE_D_MMMM_Y = 'EEEE d MMMM y';
export const FORMAT_D_MMMM_Y = 'd MMMM yyyy';
export const FORMAT_MMMM_Y = 'MMMM yyyy';
export const FORMAT_DD_MMMM = 'DD MMMM';

export const NO_DATE_FORMAT = 'DD.MM.YYYY';

export const EN_DATE_FORMAT = 'DD/MM/YYYY';
export const EN_DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
export const NO_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';
export const EN_DATE_TIME_FORMAT_HTML_TEMPLATE = 'dd/MM/YYYY HH:mm';

@Injectable({
  providedIn: 'root',
})
export class DayDate {
  static new(date: string | number | Date, template?: string, withTimezone: boolean = false): Dayjs | null {
    if (date === null || (date as string) === '') {
      return null;
    }

    // Return dayjs from Date object
    if (date instanceof Date) {
      return withTimezone ? this.fromDate(date as Date).tz() : this.fromDate(date as Date);
    }

    if (this.isIsoFormat(date)) {
      // try ISO format parsing
      return withTimezone ? this.fromISO(date).tz() : this.fromISO(date);
    } else {
      // Return days from string in current locale
      const dateFromString = dayjs(date, template ?? [NO_DATE_FORMAT, EN_DATE_FORMAT, 'YYYY']);

      if (dateFromString.isValid()) {
        return withTimezone ? dateFromString.tz() : dateFromString;
      }
    }

    return null;
  }

  static fromDate(date?: Date | string): Dayjs | null {
    const dayjsDate = date ? dayjs(date) : null;

    if (!dayjsDate || !dayjsDate.isValid()) {
      return this.new(date);
    }

    return dayjsDate;
  }

  static fromTime(time: Date | string): Dayjs {
    return dayjs(time, TIME_FORMAT);
  }

  static fromISO(date: string | number): Dayjs {
    const dateFromISOString = dayjs(date);

    if (dateFromISOString.isValid()) {
      return dateFromISOString;
    }

    return null;
  }

  static now(): Dayjs {
    return dayjs.tz();
  }

  static setLocale(locale: string) {
    const isEnGb = locale == 'en-GB';

    dayjs.locale(isEnGb ? 'en-gb' : 'nb'); // Global dayJs locale
    dayjs.tz.setDefault(getDefaultTimezone(isEnGb)); // Global dayJs timezone
  }

  static toUtc(date: Date | string, keepLocalTime?: boolean): Date {
    const tzOffset = new Date().getTimezoneOffset();

    return this.fromDate(date).utcOffset(tzOffset).utc(keepLocalTime).toDate();
  }

  static toFormat(date: Date | Dayjs, withoutTime = false): string {
    if (isNullOrUndefined(date)) {
      return null;
    }

    if (dayjs.isDayjs(date)) {
      return date.format(!withoutTime ? EN_DATE_TIME_FORMAT : EN_DATE_FORMAT);
    }

    return this.fromDate(date).format(!withoutTime ? EN_DATE_TIME_FORMAT : EN_DATE_FORMAT);
  }

  static getFormat(withoutTime = false) {
    return !withoutTime ? EN_DATE_TIME_FORMAT : EN_DATE_FORMAT;
  }

  static toTimeFormat(date: Date, utc?: boolean) {
    return utc ? this.fromDate(date).utc().format(TIME_FORMAT) : this.fromDate(date).format(TIME_FORMAT);
  }

  static parseTimeFromString(date: string | Date): string {
    let parsedTimes: string[];

    if (typeof date !== 'string') {
      const dateString = date.toTimeString();

      parsedTimes = dateString.split(':');
    } else {
      parsedTimes = date.toString().split('T')[1].split(':');
    }

    return `${parsedTimes[0]}:${parsedTimes[1]}`;
  }

  static toLocalizedFormat(date: Date, withoutTime = false, utc: boolean = false): string | null {
    if (!date) {
      return null;
    }

    if (utc) {
      return this.fromDate(date)
        .utc()
        .format(!withoutTime ? LOCALIZED_FORMAT : LOCALIZED_FORMAT_WITHOUT_TIME);
    }

    return this.new(date).format(!withoutTime ? LOCALIZED_FORMAT : LOCALIZED_FORMAT_WITHOUT_TIME);
  }

  static isIsoFormat(date: any): boolean {
    if (!date || typeof date !== 'string' || !date.includes('T')) {
      return false;
    }

    const dateParsed = new Date(Date.parse(date));

    try {
      const isoString = dateParsed.toISOString();

      return true;
    } catch (e) {
      return false;
    }
  }

  static toCustomFormat(date: Date, format: string): string {
    return this.fromDate(date).format(format);
  }

  static toIso(date: any): string {
    return this.fromDate(date).format();
  }

  static toIsoDate(date: Date): string {
    return this.fromDate(date).format(ISO_FORMAT);
  }

  static toIsoWithoutTimezone(date: any, withoutTime = false): string {
    const dayjsDate = this.fromDate(date);

    return withoutTime ? dayjsDate?.format('YYYY-MM-DD[T]00:00:00') : dayjsDate?.format('YYYY-MM-DD[T]HH:mm:ss');
  }

  static toIsoString(date: any, withoutTime = false): string {
    const dayjsDate = dayjs(date).utc();

    return withoutTime ? dayjsDate?.format('YYYY-MM-DD[T]00:00:00') : dayjsDate?.format('YYYY-MM-DD[T]HH:mm:ss');
  }

  static isEqual(dateLeft: Date | string, dateRight: Date | string): boolean {
    if (!dateLeft && !dateRight) {
      return true;
    }
    if (!dateLeft || !dateRight) {
      return false;
    }

    const date1 = this.fromDate(dateLeft);
    const date2 = this.fromDate(dateRight);

    return date1.isSame(date2);
  }

  static isSameDay(dateLeft: Date, dateRight: Date): boolean {
    if (!dateLeft && !dateRight) {
      return true;
    }
    if (!dateLeft || !dateRight) {
      return false;
    }

    const date1 = this.fromDate(dateLeft);
    const date2 = this.fromDate(dateRight);

    return date1.isSame(date2, 'day');
  }

  static mergeTimeWithDate(date: Date, time: Date): Date {
    if (!date) {
      return undefined;
    }

    if (!time) {
      return this.fromDate(date).toDate();
    }

    const jsDate = new Date(date);
    const dayTime = this.fromDate(time);

    jsDate.setHours(dayTime.hour());
    jsDate.setMinutes(dayTime.minute());

    return jsDate;
  }

  static mergeTimeWithDateComms(date: Date, time: Date): Date {
    if (date) {
      const dayDate = this.fromDate(date);
      if (time) {
        const dayTime = this.fromTime(time);

        date.setHours(dayTime.hour());
        date.setMinutes(dayTime.minute());

        return date;
      } else {
        return dayDate.toDate();
      }
    } else {
      return undefined;
    }
  }

  static duration(milliseconds: number): duration.Duration {
    return dayjs.duration(milliseconds);
  }

  static minutesBetween(dateFrom: Date, dateTo: Date): number {
    return Math.abs(this.fromDate(dateFrom).diff(this.fromDate(dateTo), 'minute'));
  }

  static getDay(date: Date): number {
    return this.fromDate(date).day();
  }

  static startOfDay(date: Date): Date {
    return this.fromDate(date).startOf('day').toDate();
  }

  static endOfDay(date: Date): Date {
    return this.fromDate(date).endOf('day').toDate();
  }

  static isToday(date: Date): boolean {
    return this.fromDate(date).isToday();
  }

  static isCurrentMonth(date: Date): boolean {
    const today = this.now();
    const comparingDate = this.fromDate(date);

    return today.month() === comparingDate.month() && today.year() === comparingDate.year();
  }

  static startOfMonth(date: Date): Date {
    return this.fromDate(date).startOf('month').toDate();
  }

  static endOfMonth(date: Date): Date {
    return this.fromDate(date).endOf('month').toDate();
  }

  static addDays(date: Date, days: number): Date {
    return this.fromDate(date).add(days, 'days').toDate();
  }

  static addWeeks(date: Date, weeks: number): Date {
    return this.fromDate(date).add(weeks, 'weeks').toDate();
  }

  static addMonths(date: Date, months: number): Date {
    return this.fromDate(date).add(months, 'months').toDate();
  }

  static addYears(date: Date, years: number): Date {
    return this.fromDate(date).add(years, 'years').toDate();
  }

  static addHours(date: Date, hours: number): Date {
    return this.fromDate(date).add(hours, 'hours').toDate();
  }

  static addMinutes(date: Date, minutes: number): Date {
    return this.fromDate(date).add(minutes, 'minutes').toDate();
  }

  static differenceInCalendarDays(dateLeft: Date, dateRight: Date): number {
    return this.fromDate(dateLeft).diff(this.fromDate(dateRight), 'days');
  }

  static getMobiscrollDateFormat(): string {
    return this.isNoLocale() ? NO_DATE_FORMAT : EN_DATE_FORMAT;
  }

  static getHTMLTemplateDateFormat(): string {
    return this.getMobiscrollDateFormat().replace(/[DY]/g, match => match.toLowerCase());
  }

  static toLocalDateTime(date: Date | string): Date {
    const localDate = DayDate.new(date, null, true);

    return this.dayJsToDate(localDate);
  }

  static toDateTime(date: string): Date | null {
    if (!date) {
      return null;
    }
    const dayjsDate = dayjs.utc(date);
    return dayjsDate.isValid() ? this.dayJsToDate(dayjsDate) : null;
  }

  static getTimezone(): string {
    const timezone = dayjs.tz()['$x']['$timezone'];

    return timezone ?? 'Europe/London';
  }

  static dayJsToDate(date?: Dayjs): Date {
    if (!date) {
      return null;
    }

    return new Date(date.year(), date.month(), date.date(), date.hour(), date.minute(), date.second());
  }

  static startOfWeek(d: Dayjs): string {
    return DayDate.toIsoDate(d.startOf('week').toDate());
  }

  static endOfWeek(d: Dayjs): string {
    return DayDate.toIsoDate(d.endOf('week').toDate());
  }

  static toDateWithoutTimezone(date: Date | string): Date {
    if (!date) {
      return null;
    }

    if (!!this.fromDate(date)) {
      return new Date(new Date(date).toISOString().slice(0, -1));
    }

    return this.timeStringToDate(date.toString());
  }

  static getDDMMYYYYHHmmPipeFormat(): string {
    return this.isNoLocale() ? `dd.MM.YYYY HH:mm` : `dd/MM/YYYY HH:mm`;
  }
  static getDDMMYYYYPipeFormat(): string {
    return this.isNoLocale() ? `dd.MM.YYYY` : `dd/MM/YYYY`;
  }

  static fromTimezoneToUtcApiString(date: Date | string): string {
    const dayDate = DayDate.new(date);
    const dayDateTimezone = dayDate.tz(DayDate.getTimezone(), true);

    const result = dayDateTimezone.toISOString();

    return result;
  }

  static isBetween(date: Date | string, dateFrom: Date | string, dateTo: Date | string): boolean {
    const toDate = (value: Date | string): Dayjs => {
      return this.fromDate(value);
    };

    const adjustedDate = toDate(date);
    const adjustedDateFrom = toDate(dateFrom);
    const adjustedDateTo = toDate(dateTo);

    const result = adjustedDate.isBetween(adjustedDateFrom, adjustedDateTo, 'day', '[]');

    return result;
  }

  static isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  }

  // Returns today's date at start of day, good to use for min date validation
  public static startOfToday() {
    return DayDate.startOfDay(new Date());
  }

  private static isNoLocale(): boolean {
    return dayjs.locale() === 'nb';
  }

  private static timeStringToDate(time: string): Date {
    var date = new Date();

    date.setHours(+time.substring(0, time.indexOf(':')));
    date.setMinutes(+time.substring(time.indexOf(':') + 1));
    date.setSeconds(0);

    return date;
  }
}
